<template>
  <ModalWindow
    :model-value="modelValue"
    :z-index="zIndex"
    max-width="920px"
    :title="step === 1 ? 'Пополнение баланса' : 'Оплата'"
    @close="emit('close')"
  >
    <div class="balance-modal">
      <div class="balance-modal__caption">Текущий баланс: <GeniInline :amount="currentBalance" size="xs" /></div>

      <div v-if="step === 1" class="balance-modal__packs">
        <article
          v-for="pack in paidPacks"
          :key="pack.id"
          class="balance-modal__pack"
          :class="{
            'is-studio': pack.id === STUDIO_PACK_ID,
            'is-business': pack.id === BUSINESS_PACK_ID,
          }"
        >
          <div class="balance-modal__pack-head">
            <div class="balance-modal__pack-title">{{ pack.label }}</div>
            <span v-if="packSavingsPercent(pack) > 0" class="balance-modal__pack-discount-badge">
              СКИДКА −{{ packSavingsPercent(pack) }}%
            </span>
          </div>
          <div class="balance-modal__pack-pricing">
            <div class="balance-modal__mini-card">
              <p class="balance-modal__mini-card-label">За 1 фото</p>
              <div class="balance-modal__price-row balance-modal__price-row--photo">
                <span class="balance-modal__photo-hero-price">{{ formatKzt(photoPriceKzt(pack)) }}</span>
                <span v-if="packSavingsPercent(pack) > 0" class="balance-modal__price-strike">{{ formatKzt(startPhotoPriceKzt) }}</span>
              </div>
            </div>
            <div class="balance-modal__mini-card">
              <p class="balance-modal__mini-card-label">За пакет</p>
              <div class="balance-modal__price-row balance-modal__price-row--pack">
                <span class="balance-modal__pack-price">{{ formatKzt(pack.rub) }}</span>
                <span v-if="packSavingsPercent(pack) > 0" class="balance-modal__price-strike">{{ formatKzt(packListPriceKzt(pack)) }}</span>
              </div>
              <div class="balance-modal__pack-content-row">
                <GeniInline :amount="pack.geni" size="sm" theme="inherit" :animate-icon="pack.id === STUDIO_PACK_ID || pack.id === BUSINESS_PACK_ID" />
                <span class="balance-modal__pack-content-sep">·</span>
                <span>до {{ pack.geni }} фото</span>
              </div>
            </div>
          </div>
          <div class="balance-modal__pack-cta">
            <Button
              block
              size="sm"
              :variant="pack.id === STUDIO_PACK_ID || pack.id === BUSINESS_PACK_ID ? 'primary' : 'outline'"
              :class-name="pack.id === STUDIO_PACK_ID || pack.id === BUSINESS_PACK_ID ? 'balance-modal__choose-btn--value' : ''"
              @click="goToPayment(pack.id)"
            >
              Выбрать пакет
            </Button>
          </div>
        </article>
      </div>

      <div v-else-if="selectedPack" class="balance-modal__payment">
        <div class="balance-modal__summary-grid">
          <span>Пакет</span><strong>{{ selectedPack.label }}</strong>
          <span>Баланс</span><strong><GeniInline :amount="selectedPack.geni" size="xs" /></strong>
          <span>Цена</span><strong>{{ selectedPack.rub }} ₸</strong>
        </div>

        <div class="balance-modal__promo">
          <button
            type="button"
            class="balance-modal__promo-toggle"
            :aria-expanded="promoExpanded"
            aria-controls="balance-modal-promo-panel"
            @click="promoExpanded = !promoExpanded"
          >
            {{ promoExpanded ? "Свернуть" : "Есть промкод?" }}
          </button>
          <div
            id="balance-modal-promo-panel"
            class="balance-modal__promo-panel"
            :class="{ 'is-open': promoExpanded }"
            :aria-hidden="!promoExpanded"
          >
            <div class="balance-modal__promo-panel-inner">
              <label class="balance-modal__label">
                Промокод
                <div class="balance-modal__promo-row">
                  <input v-model="promo" type="text" placeholder="Код или ссылка" />
                  <button type="button" class="balance-modal__promo-apply" aria-label="Применить промокод">
                    <Icon name="arrow-right" :size="16" />
                  </button>
                </div>
              </label>
            </div>
          </div>
        </div>

        <div class="balance-modal__total">
          <div class="balance-modal__total-row"><span>Geni</span><strong><GeniInline :amount="selectedPack.geni" size="xs" /></strong></div>
          <div class="balance-modal__total-row balance-modal__total-row--final"><span>Итого</span><strong>{{ selectedPack.rub }} ₸</strong></div>
        </div>

        <div class="balance-modal__actions">
          <button class="balance-modal__back-btn" type="button" @click="step = 1">Назад к пакетам</button>
          <button class="balance-modal__pay-btn" type="button" :disabled="isPaying" @click="handleSubmit">
            <span aria-hidden="true" class="balance-modal__pay-electric">
              <span class="balance-modal__pay-electric-halo"></span>
              <span class="balance-modal__pay-electric-spark balance-modal__pay-electric-spark-a"></span>
              <span class="balance-modal__pay-electric-spark balance-modal__pay-electric-spark-b"></span>
              <span class="balance-modal__pay-electric-spark balance-modal__pay-electric-spark-c"></span>
              <span class="balance-modal__pay-electric-spark balance-modal__pay-electric-spark-d"></span>
              <Icon name="lightning" :size="14" class="balance-modal__pay-electric-bolt" />
            </span>
            <span>{{ isPaying ? "Оплачиваем..." : "Оплатить" }}</span>
          </button>
        </div>
      </div>
    </div>
  </ModalWindow>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import ModalWindow from "@/components/modal/ModalWindow.vue";
import GeniInline from "@/components/billing/GeniInline.vue";
import Button from "@/components/ui/Button.vue";
import Icon from "@/components/ui/Icon.vue";
import type { GeniPack } from "@/api/billing";
import { captureEvent } from "@/lib/analytics";

const START_PACK_ID = "pack_60";
const STUDIO_PACK_ID = "pack_120";
const BUSINESS_PACK_ID = "pack_240";

function photoPriceKzt(pack: GeniPack): number {
  return pack.geni > 0 ? Math.round(pack.rub / pack.geni) : 0;
}

function formatKzt(amount: number): string {
  return `${amount.toLocaleString("ru-KZ")} ₸`;
}

function savingsPercent(pack: GeniPack, startPhotoPrice: number): number {
  if (startPhotoPrice <= 0) return 0;
  const price = photoPriceKzt(pack);
  if (price >= startPhotoPrice) return 0;
  return Math.round((1 - price / startPhotoPrice) * 100);
}

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    packs: GeniPack[];
    currentBalance: number;
    isPaying?: boolean;
    initialPackId?: string | null;
    /**
     * Опциональный z-index для оверлея. Используется при открытии BalanceModal поверх
     * другой модалки (например, из InsufficientGeniModal внутри full-screen CreateGoodDialog).
     */
    zIndex?: number;
  }>(),
  { isPaying: false, initialPackId: null, zIndex: undefined }
);

const emit = defineEmits<{
  (e: "close"): void;
  (e: "pay", pack: GeniPack): void;
}>();

const step = ref<1 | 2>(1);
const selectedPackId = ref<string | null>(null);
const promo = ref("");
const promoExpanded = ref(false);

const paidPacks = computed(() => props.packs.filter((pack) => pack.id !== "free"));
const selectedPack = computed(() => paidPacks.value.find((pack) => pack.id === selectedPackId.value) ?? null);
const startPhotoPriceKzt = computed(() => {
  const start = paidPacks.value.find((pack) => pack.id === START_PACK_ID);
  return start ? photoPriceKzt(start) : 0;
});

function packSavingsPercent(pack: GeniPack): number {
  return savingsPercent(pack, startPhotoPriceKzt.value);
}

function packListPriceKzt(pack: GeniPack): number {
  if (pack.geni <= 0 || startPhotoPriceKzt.value <= 0) return 0;
  return pack.geni * startPhotoPriceKzt.value;
}

watch(
  () => props.modelValue,
  (opened) => {
    if (!opened) return;
    selectedPackId.value = props.initialPackId && paidPacks.value.some((pack) => pack.id === props.initialPackId) ? props.initialPackId : null;
    step.value = 1;
    promo.value = "";
    promoExpanded.value = false;
  }
);

watch(step, (nextStep) => {
  if (nextStep === 1) promoExpanded.value = false;
});

function selectPack(packId: string) {
  selectedPackId.value = packId;
}

function goToPayment(packId: string) {
  const pack = paidPacks.value.find((p) => p.id === packId);
  if (pack) {
    captureEvent("[CardGenius] Пополнение выбран тариф", {
      pack_id: pack.id,
      pack_label: pack.label,
      geni: pack.geni,
      price_kzt: pack.rub,
    });
  }
  selectedPackId.value = packId;
  step.value = 2;
}

function handleSubmit() {
  if (step.value === 1) {
    if (!selectedPackId.value) return;
    step.value = 2;
    return;
  }
  if (!selectedPack.value) return;
  captureEvent("[CardGenius] Пополнение клик оплатить", {
    pack_id: selectedPack.value.id,
    pack_label: selectedPack.value.label,
    geni: selectedPack.value.geni,
    price_kzt: selectedPack.value.rub,
  });
  emit("pay", selectedPack.value);
}
</script>

<style scoped lang="scss">
:deep(.modal-card[aria-label="Оплата"]),
:deep(.modal-card[aria-label="Пополнение баланса"]) {
  background: var(--card) !important;
}

:deep(.modal-card[aria-label="Оплата"] .modal-body),
:deep(.modal-card[aria-label="Пополнение баланса"] .modal-body) {
  background: var(--card) !important;
}

.balance-modal {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.balance-modal__caption {
  padding: 12px 20px 0;
  font-size: 12px;
  color: var(--muted-foreground);
  margin-bottom: 12px;
}
.balance-modal__packs {
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr;
  grid-auto-rows: 1fr;
  padding: 0 20px 20px;
}
.balance-modal__pack {
  position: relative;
  min-height: 220px;
  border: 1px solid color-mix(in oklch, var(--border) 60%, transparent);
  border-radius: 16px;
  background: var(--card);
  color: var(--foreground);
  padding: 20px;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.balance-modal__pack:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 30px -20px rgba(0,0,0,.35);
}
.dark .balance-modal__pack {
  background: var(--color-surface-secondary);
}
.balance-modal__pack.is-studio {
  border-color: color-mix(in oklch, var(--color-brand-secondary) 40%, transparent);
  background: var(--color-base-black);
  color: #fff;
}
.dark .balance-modal__pack.is-studio {
  border-color: color-mix(in oklch, var(--border) 60%, transparent);
  background: var(--color-base-white);
  color: var(--color-base-black);
}
.dark .balance-modal__pack.is-studio .balance-modal__pack-title {
  color: var(--color-base-black);
}
.balance-modal__pack.is-business {
  border: 0;
  background: color-mix(in oklch, var(--color-brand-primary) 6%, white);
  color: var(--foreground);
  box-shadow: 0 12px 28px -18px color-mix(in oklch, var(--color-brand-primary) 35%, transparent);
}
.dark .balance-modal__pack.is-business {
  background: color-mix(in oklch, var(--color-brand-primary) 14%, var(--color-base-black));
  color: var(--foreground);
}
.balance-modal__pack-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 20px;
}

.balance-modal__pack-title {
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
}
.balance-modal__pack-discount-badge {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid var(--color-brand-secondary);
  padding: 4px 10px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: var(--color-brand-secondary);
  color: var(--color-base-black);
  box-shadow: 0 6px 16px -4px var(--color-brand-secondary);
}
.balance-modal__pack.is-business .balance-modal__pack-discount-badge {
  border-color: var(--color-brand-primary);
  background: var(--color-brand-primary);
  color: #fff;
  box-shadow: 0 6px 16px -4px var(--color-brand-primary);
}
.balance-modal__pack-pricing {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.balance-modal__mini-card {
  border: 1px solid color-mix(in oklch, var(--border) 30%, transparent);
  background: color-mix(in oklch, var(--card) 60%, white);
  border-radius: 6px;
  padding: 10px;
}
.dark .balance-modal__pack:not(.is-studio):not(.is-business) .balance-modal__mini-card {
  background: var(--color-surface-tertiary);
}
.balance-modal__pack.is-studio .balance-modal__mini-card {
  border-color: rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
}
.dark .balance-modal__pack.is-studio .balance-modal__mini-card {
  border-color: color-mix(in oklch, var(--border) 30%, transparent);
  background: rgba(0, 0, 0, 0.03);
}
.balance-modal__pack.is-business .balance-modal__mini-card {
  border: 0;
  background: rgb(255 255 255 / 0.7);
}
.dark .balance-modal__pack.is-business .balance-modal__mini-card {
  background: rgb(255 255 255 / 0.06);
}
.balance-modal__mini-card-label {
  margin: 0;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted-foreground);
}
.balance-modal__pack.is-studio .balance-modal__mini-card-label {
  color: rgba(255, 255, 255, 0.55);
}
.dark .balance-modal__pack.is-studio .balance-modal__mini-card-label {
  color: rgba(11, 11, 15, 0.5);
}
.balance-modal__price-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px;
}
.balance-modal__price-row--photo {
  margin-top: 6px;
  min-height: 32px;
}
.balance-modal__price-row--pack {
  margin-top: 6px;
  min-height: 22px;
}
.balance-modal__photo-hero-price {
  font-size: 1.75rem;
  line-height: 1;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--foreground);
}
@media (min-width: 640px) {
  .balance-modal__photo-hero-price {
    font-size: 2rem;
  }
}
.balance-modal__pack.is-studio .balance-modal__photo-hero-price {
  color: var(--color-brand-secondary);
}
.dark .balance-modal__pack.is-studio .balance-modal__photo-hero-price {
  color: var(--color-brand-primary);
}
.balance-modal__pack.is-business .balance-modal__photo-hero-price {
  color: var(--color-brand-primary);
}
.balance-modal__price-strike {
  padding-bottom: 2px;
  font-size: 14px;
  line-height: 1;
  text-decoration: line-through;
  color: color-mix(in oklch, var(--muted-foreground) 75%, transparent);
}
.balance-modal__pack.is-studio .balance-modal__price-strike {
  color: rgba(255, 255, 255, 0.5);
}
.dark .balance-modal__pack.is-studio .balance-modal__price-strike {
  color: rgba(11, 11, 15, 0.45);
}
.balance-modal__pack-price {
  font-size: 16px;
  line-height: 1;
  font-weight: 600;
  color: var(--foreground);
}
.balance-modal__pack.is-studio .balance-modal__pack-price {
  color: rgba(255, 255, 255, 0.9);
}
.dark .balance-modal__pack.is-studio .balance-modal__pack-price {
  color: rgba(11, 11, 15, 0.85);
}
.balance-modal__pack-content-row {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid color-mix(in oklch, var(--border) 30%, transparent);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: color-mix(in oklch, var(--foreground) 85%, transparent);
}
.balance-modal__pack.is-studio .balance-modal__pack-content-row {
  border-top-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.85);
}
.dark .balance-modal__pack.is-studio .balance-modal__pack-content-row {
  border-top-color: color-mix(in oklch, var(--border) 30%, transparent);
  color: rgba(11, 11, 15, 0.8);
}
.dark .balance-modal__pack.is-studio .balance-modal__pack-content-sep {
  color: rgba(11, 11, 15, 0.55);
}
.balance-modal__pack.is-business .balance-modal__pack-content-row {
  border-top-color: color-mix(in oklch, var(--color-brand-primary) 10%, transparent);
  color: color-mix(in oklch, var(--foreground) 85%, transparent);
}
.balance-modal__pack-content-sep {
  color: var(--muted-foreground);
}
.balance-modal__pack.is-studio .balance-modal__pack-content-sep {
  color: rgba(255, 255, 255, 0.55);
}
.balance-modal__pack-cta {
  margin-top: auto;
  padding-top: 16px;
}
.balance-modal__pack-cta :deep(.cg-btn) {
  min-width: 0;
  border-radius: 12px;
}
.balance-modal__pack-cta :deep(.balance-modal__choose-btn--value.cg-btn--primary) {
  background: var(--color-brand-primary);
  color: #fff;
  border-color: transparent;
}
.balance-modal__pack-cta :deep(.balance-modal__choose-btn--value.cg-btn--primary:hover) {
  background: color-mix(in oklch, var(--color-brand-primary) 90%, transparent);
  color: #fff;
}
.balance-modal__pack-cta :deep(.balance-modal__choose-btn--value.cg-btn--primary:active) {
  background: color-mix(in oklch, var(--color-brand-primary) 80%, transparent);
  color: #fff;
}
.balance-modal__payment {
  display: grid;
  gap: 14px;
  padding: 12px 20px 20px;
}
.balance-modal__summary-grid {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px 12px;
  font-size: 14px;
}
.balance-modal__summary-grid > span { color: var(--muted-foreground); }
.balance-modal__summary-grid > strong { justify-self: end; color: var(--foreground); }
.balance-modal__summary-grid :deep(.geni-inline) { justify-self: end; }
.balance-modal__promo {
  display: grid;
  gap: 0;
}
.balance-modal__promo-toggle {
  justify-self: start;
  padding: 0;
  border: 0;
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  color: var(--muted-foreground);
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: var(--foreground);
  }
}
.balance-modal__promo-panel {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.32s cubic-bezier(0.4, 0, 0.2, 1);
}
.balance-modal__promo-panel.is-open {
  grid-template-rows: 1fr;
}
.balance-modal__promo-panel-inner {
  overflow: hidden;
  min-height: 0;
}
.balance-modal__promo-panel.is-open .balance-modal__promo-panel-inner {
  padding-top: 10px;
}
.balance-modal__promo-panel.is-open .balance-modal__label {
  opacity: 1;
  transform: translateY(0);
}
.balance-modal__label {
  display: grid;
  gap: 6px;
  font-size: 12px;
  color: var(--muted-foreground);
  opacity: 0;
  transform: translateY(-4px);
  transition:
    opacity 0.24s ease 0.04s,
    transform 0.32s cubic-bezier(0.4, 0, 0.2, 1) 0.04s;
}
.balance-modal__label input,
.balance-modal__promo-row input {
  height: 40px;
  border-radius: 10px;
  border: 1px solid color-mix(in oklch, var(--border) 80%, transparent);
  background: transparent;
  padding: 0 12px;
  color: var(--foreground);
  width: 100%;
}
.balance-modal__promo-row { display: flex; gap: 8px; }
.balance-modal__promo-apply {
  width: 40px; height: 40px; border-radius: 10px;
  border: 1px solid color-mix(in oklch, var(--border) 80%, transparent);
  background: transparent; color: var(--foreground);
  display: inline-flex; align-items: center; justify-content: center;
}
.balance-modal__total {
  border-top: 1px solid color-mix(in oklch, var(--border) 70%, transparent);
  padding-top: 12px;
  display: grid;
  gap: 8px;
}
.balance-modal__total-row { display: flex; align-items: center; justify-content: space-between; }
.balance-modal__total-row span { color: var(--muted-foreground); font-size: 14px; }
.balance-modal__total-row strong { color: var(--foreground); font-size: 15px; }
.balance-modal__total-row--final span { font-size: 30px; line-height: 1; font-weight: 600; color: var(--foreground); }
.balance-modal__total-row--final strong { font-size: 34px; line-height: 1; font-weight: 700; }
.balance-modal__actions {
  width: 100%;
  display: flex;
  gap: 10px;
  justify-content: space-between;
}
.balance-modal__back-btn,
.balance-modal__pay-btn {
  border-radius: 10px;
  height: 40px;
  border: 1px solid color-mix(in oklch, var(--border) 80%, transparent);
  background: var(--secondary);
  color: var(--foreground);
  padding: 0 16px;
  font-weight: 600;
}
.balance-modal__pay-btn {
  margin-left: auto;
  background: var(--foreground);
  color: var(--background);
  border: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.balance-modal__pay-btn:disabled {
  opacity: 0.9;
  cursor: default;
}

.balance-modal__pay-electric {
  position: relative;
  display: inline-flex;
  width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
}

.balance-modal__pay-electric-bolt {
  position: relative;
  z-index: 2;
  color: var(--color-brand-primary);
}

.balance-modal__pay-electric-halo {
  position: absolute;
  inset: 1px;
  border-radius: 999px;
  background: radial-gradient(circle, color-mix(in oklab, var(--color-brand-primary) 42%, white) 0%, transparent 68%);
  opacity: 0.2;
  transform: translateY(1px) scale(0.9);
}

.balance-modal__pay-electric-spark {
  position: absolute;
  width: 8px;
  height: 11px;
  clip-path: polygon(38% 0%, 82% 0%, 57% 35%, 100% 35%, 30% 100%, 46% 58%, 8% 58%);
  box-shadow: 0 0 10px var(--cg-spark-color, #ffcc00);
  opacity: 0;
}

.balance-modal__pay-electric-spark-a {
  --cg-spark-color: #ffcc00;
  top: 1px;
  right: -1px;
  transform-origin: 40% 55%;
  background: var(--cg-spark-color);
}

.balance-modal__pay-electric-spark-b {
  --cg-spark-color: #ff453a;
  bottom: 1px;
  left: -2px;
  transform-origin: 52% 48%;
  background: var(--cg-spark-color);
}

.balance-modal__pay-electric-spark-c {
  --cg-spark-color: #ff453a;
  top: -1px;
  left: 3px;
  transform-origin: 50% 50%;
  background: var(--cg-spark-color);
}

.balance-modal__pay-electric-spark-d {
  --cg-spark-color: #ffcc00;
  bottom: -1px;
  right: 2px;
  transform-origin: 50% 50%;
  background: var(--cg-spark-color);
}

.balance-modal__pay-btn:hover:not(:disabled) .balance-modal__pay-electric-halo {
  animation: balance-pay-halo-pulse 1.3s ease-in-out infinite;
}

.balance-modal__pay-btn:hover:not(:disabled) .balance-modal__pay-electric-spark-a {
  animation: balance-pay-spark-a 1.05s steps(5, end) infinite;
}

.balance-modal__pay-btn:hover:not(:disabled) .balance-modal__pay-electric-spark-b {
  animation: balance-pay-spark-b 1.05s steps(5, end) infinite;
}

.balance-modal__pay-btn:hover:not(:disabled) .balance-modal__pay-electric-spark-c {
  animation: balance-pay-spark-c 1.05s steps(5, end) infinite;
}

.balance-modal__pay-btn:hover:not(:disabled) .balance-modal__pay-electric-spark-d {
  animation: balance-pay-spark-d 1.05s steps(5, end) infinite;
}

@keyframes balance-pay-halo-pulse {
  0%,
  100% {
    opacity: 0.35;
    transform: scale(0.86);
  }
  40% {
    opacity: 0.7;
    transform: scale(1.08);
  }
  65% {
    opacity: 0.45;
    transform: scale(0.95);
  }
}

@keyframes balance-pay-spark-a {
  0%,
  100% {
    opacity: 0;
    transform: translate(0, 0) rotate(8deg) scale(0.6);
  }
  8% {
    opacity: 1;
    transform: translate(1px, -1px) rotate(16deg) scale(1);
  }
  14% {
    opacity: 0;
    transform: translate(4px, -3px) rotate(22deg) scale(0.8);
  }
  24% {
    opacity: 0.95;
    transform: translate(2px, -2px) rotate(14deg) scale(0.96);
  }
  31% {
    opacity: 0;
    transform: translate(6px, -4px) rotate(23deg) scale(0.7);
  }
}

@keyframes balance-pay-spark-b {
  0%,
  100% {
    opacity: 0;
    transform: translate(0, 0) rotate(-12deg) scale(0.6);
  }
  42% {
    opacity: 0;
    transform: translate(0, 0) rotate(-12deg) scale(0.6);
  }
  52% {
    opacity: 1;
    transform: translate(-1px, 1px) rotate(-22deg) scale(1);
  }
  58% {
    opacity: 0;
    transform: translate(-4px, 3px) rotate(-26deg) scale(0.8);
  }
  68% {
    opacity: 0.9;
    transform: translate(-2px, 2px) rotate(-20deg) scale(0.94);
  }
  75% {
    opacity: 0;
    transform: translate(-6px, 5px) rotate(-28deg) scale(0.72);
  }
}

@keyframes balance-pay-spark-c {
  0%,
  100% {
    opacity: 0;
    transform: translate(0, 0) rotate(2deg) scale(0.58);
  }
  30% {
    opacity: 0;
    transform: translate(0, 0) rotate(2deg) scale(0.58);
  }
  36% {
    opacity: 0.95;
    transform: translate(1px, -2px) rotate(10deg) scale(0.98);
  }
  43% {
    opacity: 0;
    transform: translate(3px, -5px) rotate(16deg) scale(0.72);
  }
}

@keyframes balance-pay-spark-d {
  0%,
  100% {
    opacity: 0;
    transform: translate(0, 0) rotate(-4deg) scale(0.58);
  }
  60% {
    opacity: 0;
    transform: translate(0, 0) rotate(-4deg) scale(0.58);
  }
  67% {
    opacity: 0.95;
    transform: translate(1px, 2px) rotate(-11deg) scale(0.98);
  }
  74% {
    opacity: 0;
    transform: translate(3px, 5px) rotate(-18deg) scale(0.72);
  }
}

@media (prefers-reduced-motion: reduce) {
  .balance-modal__pay-btn:hover:not(:disabled) .balance-modal__pay-electric-halo,
  .balance-modal__pay-btn:hover:not(:disabled) .balance-modal__pay-electric-spark {
    animation: none !important;
  }

  .balance-modal__promo-panel,
  .balance-modal__label {
    transition: none !important;
  }
}
@media (min-width: 640px) {
  .balance-modal__packs {
    gap: 12px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .balance-modal__total-row--final span { font-size: 26px; }
  .balance-modal__total-row--final strong { font-size: 30px; }
}

@media (max-width: 640px) {
  :deep(.modal-card.modal-card--sheet-mobile .modal-body) {
    display: flex;
    flex-direction: column;
  }

  .balance-modal__payment {
    min-height: 100%;
    display: flex;
    flex-direction: column;
  }

  .balance-modal__actions {
    position: sticky;
    bottom: 0;
    margin-top: auto;
    padding-top: 10px;
    padding-bottom: calc(8px + env(safe-area-inset-bottom));
    background: var(--card);
    border-top: 1px solid color-mix(in oklch, var(--border) 70%, transparent);
  }
}
</style>
